import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { displayStyle } from '../board/ui';
import { bulbTexture, glowTexture } from '../road/glow';

/** A dark bulb is warm brown, like the wood it sits in; a lit one gold. */
const OFF = 0x86552a;
const LIT = 0xffe9a0;
const GLOW = 0xffb93d;
const BRASS = 0xe6d2b0;
/** The bezel art's hole is 71% of its width; the bulb tucks just under it. */
const BULB_OF_BEZEL = 0.74;

interface Lamp {
  socket: Sprite;
  bulb: Sprite;
  bezel: Sprite;
  glow: Sprite;
  size: number;
}

/**
 * Whack's prize ladder: a row of lamps in brass bezels, one per hit up to
 * the top prize, lighting gold as the moles are whacked. The lamps where
 * the prize steps up are bigger and carry their prize over them, so each
 * hit visibly moves the player towards more coins. Tapping it says how.
 */
export class WhackMeter extends Container {
  private lamps: Lamp[] = [];
  private labels: Text[] = [];
  private bands: number[] = [];
  private lit = 0;
  /** A prize lamp just reached glows brighter for a moment. */
  private flare = 0;
  private flareAt = -1;

  constructor(
    private readonly bezel: () => Texture | null,
    onTap: () => void,
  ) {
    super();
    this.eventMode = 'static';
    this.cursor = 'help';
    this.on('pointertap', onTap);
  }

  get rowHeight(): number {
    return 46;
  }

  /** Build the lamps for `bands` (hits where the prize steps up) and
   *  `pays`, and lay them across `width`. */
  build(bands: readonly number[], pays: readonly number[], width: number): void {
    this.removeChildren().forEach((c) => c.destroy());
    this.bands = [...bands];
    const top = Math.max(1, bands[bands.length - 1] ?? 1);
    const step = Math.min(26, (width - 24) / top);
    const base = Math.max(9, step * 0.86);
    const left = (width - step * (top - 1)) / 2;
    const y = this.rowHeight - base * 0.75;
    const bezel = this.bezel();
    this.lamps = Array.from({ length: top }, (_, i) => {
      const prize = bands.includes(i + 1);
      const size = prize ? base * 1.4 : base;
      const socket = new Sprite(glowTexture());
      const bulb = new Sprite(bulbTexture());
      const ring = new Sprite();
      const glow = new Sprite(glowTexture());
      for (const s of [socket, bulb, ring, glow]) {
        s.anchor.set(0.5);
        s.position.set(left + i * step, y);
      }
      socket.tint = 0x000000;
      socket.alpha = 0.6;
      socket.width = socket.height = size * 1.3;
      ring.tint = BRASS;
      ring.visible = !!bezel;
      if (bezel) ring.texture = bezel;
      ring.width = ring.height = size;
      bulb.width = bulb.height = size * (bezel ? BULB_OF_BEZEL : 0.82);
      glow.blendMode = 'add';
      glow.tint = GLOW;
      glow.width = glow.height = size * 2.6;
      this.addChild(socket, bulb, ring, glow);
      return { socket, bulb, bezel: ring, glow, size };
    });
    this.labels = bands
      .map((at, i) => ({ at, pay: pays[i] ?? 0 }))
      .filter((b) => b.at > 0)
      .map((b) => {
        const t = new Text({ text: b.pay.toLocaleString('en-GB'), style: displayStyle(Math.round(Math.max(13, base * 0.82))) });
        t.anchor.set(0.5, 1);
        t.position.set(left + (b.at - 1) * step, y - base * 0.9);
        this.addChild(t);
        return t;
      });
    this.hitArea = { contains: (px: number, py: number) => px >= 0 && px <= width && py >= 0 && py <= this.rowHeight + 4 };
    this.paint();
  }

  /** Light the first `hits` lamps. A prize lamp reached flares. */
  set(hits: number): void {
    const before = this.lit;
    this.lit = hits;
    if (hits > before && this.bands.includes(hits)) {
      this.flare = 1;
      this.flareAt = hits - 1;
    }
    this.paint();
  }

  /** Per frame: the flare fades, and a prize lamp not yet reached breathes
   *  a little, to draw the eye. */
  tick(deltaMs: number, clock: number): void {
    if (this.flare > 0) this.flare = Math.max(0, this.flare - deltaMs / 700);
    const next = this.bands.find((b) => b > this.lit);
    this.lamps.forEach((lamp, i) => {
      const lit = i < this.lit;
      let glow = lit ? 0.35 : 0;
      if (i === this.flareAt) glow += this.flare * 0.9;
      if (!lit && next !== undefined && i === next - 1) glow = 0.12 + 0.12 * Math.sin(clock * 5);
      lamp.glow.alpha = glow;
    });
  }

  private paint(): void {
    this.lamps.forEach((lamp, i) => {
      lamp.bulb.tint = i < this.lit ? LIT : OFF;
      lamp.glow.alpha = i < this.lit ? 0.35 : 0;
    });
    // A prize reached: its label turns bright.
    let k = 0;
    this.bands.forEach((at) => {
      if (at <= 0) return;
      const label = this.labels[k++];
      if (label) label.alpha = this.lit >= at ? 1 : 0.8;
    });
  }
}
